import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSurveyStore } from './surveyutilities/surveyStore';
import SurveyHeader from './viewerparts/SurveyHeader';
import ProgressBar from './viewerparts/ProgressBar';
import Navigation from './viewerparts/Navigation';
import SubmitMessage from './viewerparts/SubmitMessage.jsx';
import TextQuestionBlock from './viewerparts/questionBlocks/TextQuestionBlock';
import TextAreaQuestionBlock from './viewerparts/questionBlocks/TextAreaQuestionBlock';
import DropdownQuestionBlock from './viewerparts/questionBlocks/DropdownQuestionBlock';
import SingleChoiceQuestionBlock from './viewerparts/questionBlocks/SingleChoiceQuestionBlock';
import MultipleChoiceQuestionBlock from './viewerparts/questionBlocks/MultipleChoiceQuestionBlock';
import RatingQuestionBlock from './viewerparts/questionBlocks/RatingQuestionBlock';

const EcxSurveyViewer = ({ theme }) => {
    const survey = useSurveyStore((state) => state.survey);
    const questions = useSurveyStore((state) => state.questions);
    const {
        register,
        handleSubmit,
        watch,
        trigger,
        setValue,
        formState: { errors },
    } = useForm();

    const [formValues, setFormValues] = useState({});
    const [currentPage, setCurrentPage] = useState(0);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submittedData, setSubmittedData] = useState(null);

    useEffect(() => {
        const subscription = watch((value) => {
            setFormValues(value || {});
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    const showSinglePage = Boolean(
        survey?.singleQuestionPerPage === true || survey?.singleQuestionPerPage === 'true'
    );

    const answeredCount = useMemo(() => {
        if (!questions?.length) return 0;
        return questions.reduce((count, question) => {
            const value = formValues[question.internalId];
            if (value === undefined || value === null || value === '') return count;
            if (Array.isArray(value) && value.length === 0) return count;
            return count + 1;
        }, 0);
    }, [formValues, questions]);

    const onSubmit = (data, event) => {
        event?.preventDefault();
        const isLastPage = currentPage === (questions?.length || 1) - 1;
        if (isLastPage || !showSinglePage) {
            setSubmittedData(data);
            setIsSubmitted(true);
        }
    };

    const isCurrentPageValid = async () => {
        if (!questions?.[currentPage]?.required) return true;
        return trigger(questions[currentPage].internalId);
    };

    const handleNext = async () => {
        const isValid = await isCurrentPageValid();
        if (isValid) {
            setCurrentPage((prev) => Math.min(prev + 1, questions.length - 1));
        }
    };

    const handlePrevious = () => {
        setCurrentPage((prev) => Math.max(prev - 1, 0));
    };

    const calculateProgress = () => {
        if (!questions?.length) return 0;
        if (survey.progressbarType === 'STEPS') {
            return {
                current: currentPage + 1,
                total: questions.length,
            };
        }
        return Math.round(((currentPage + 1) / questions.length) * 100);
    };

    const renderQuestion = (q) => {
        const commonProps = { question: q, register, errors, watch, setValue };

        switch (q.type) {
            case 'text':
                return <TextQuestionBlock {...commonProps} />;
            case 'textarea':
                return <TextAreaQuestionBlock {...commonProps} />;
            case 'dropdown':
                return <DropdownQuestionBlock {...commonProps} />;
            case 'single_choice':
                return <SingleChoiceQuestionBlock {...commonProps} />;
            case 'multiple_choice':
                return <MultipleChoiceQuestionBlock {...commonProps} />;
            case 'rating':
                return <RatingQuestionBlock {...commonProps} />;
            default:
                return null;
        }
    };

    if (!survey) {
        return (
            <div className="ecx-survey-preview">
                <div className="ecx-survey-preview__loading">
                    <div className="spinner-border text-primary" role="status" />
                    <p className="mb-0 mt-2 text-muted">Loading survey…</p>
                </div>
            </div>
        );
    }

    if (isSubmitted) {
        return <SubmitMessage survey={survey} submittedData={submittedData} questions={questions} />;
    }

    const currentQuestion = showSinglePage ? questions[currentPage] : null;

    return (
        <div className="ecx-survey-preview">
            <div className="ecx-survey-preview__layout">
                <section className="ecx-survey-preview__form-panel">
                    <div className="ecx-survey-preview__paper">
                        <div className="ecx-survey-preview__badge">
                            <i className="bi bi-eye" />
                            Patient preview
                        </div>

                        <SurveyHeader survey={survey} theme={theme} />

                        {showSinglePage && survey.showProgressbar ? (
                            <ProgressBar survey={survey} progress={calculateProgress()} />
                        ) : null}

                        <form onSubmit={handleSubmit(onSubmit)} noValidate>
                            {showSinglePage ? (
                                <div className="ecx-survey-preview__questions">
                                    {currentQuestion ? (
                                        <div key={currentQuestion.internalId || currentQuestion.id}>
                                            {renderQuestion(currentQuestion)}
                                        </div>
                                    ) : (
                                        <div className="ecx-survey-preview__empty">
                                            No questions to preview yet.
                                        </div>
                                    )}
                                    <Navigation
                                        isFirstPage={currentPage === 0}
                                        isLastPage={currentPage === (questions?.length || 1) - 1}
                                        onPrevious={handlePrevious}
                                        onNext={handleNext}
                                    />
                                </div>
                            ) : (
                                <div className="ecx-survey-preview__questions">
                                    {questions?.length ? (
                                        questions.map((q) => (
                                            <div
                                                key={q.internalId || q.id}
                                                className="ecx-survey-preview__question-card"
                                            >
                                                {renderQuestion(q)}
                                            </div>
                                        ))
                                    ) : (
                                        <div className="ecx-survey-preview__empty">
                                            No questions to preview yet.
                                        </div>
                                    )}
                                    {questions?.length ? (
                                        <div className="ecx-survey-preview__nav">
                                            <button
                                                type="submit"
                                                className="btn btn-sm btn-primary rounded-pill"
                                            >
                                                <i className="bi bi-send-check me-1" />
                                                Submit survey
                                            </button>
                                        </div>
                                    ) : null}
                                </div>
                            )}
                        </form>
                    </div>
                </section>

                <aside className="ecx-survey-preview__side-panel">
                    <div className="ecx-survey-preview__side-card">
                        <div className="ecx-survey-preview__side-header">
                            <div>
                                <h3 className="ecx-survey-preview__side-title">Live answers</h3>
                                <p className="ecx-survey-preview__side-meta mb-0">
                                    {answeredCount} of {questions?.length || 0} answered
                                </p>
                            </div>
                            <span className="ecx-page-chip">{questions?.length || 0} Q</span>
                        </div>
                        <div className="ecx-survey-preview__side-body">
                            {questions?.length ? (
                                <ul className="ecx-survey-preview__answer-list">
                                    {questions.map((question, index) => {
                                        const value = formValues[question.internalId];
                                        const hasValue =
                                            value !== undefined &&
                                            value !== null &&
                                            value !== '' &&
                                            !(Array.isArray(value) && value.length === 0);
                                        return (
                                            <li key={question.internalId || question.id}>
                                                <div className="ecx-survey-preview__answer-label">
                                                    <span>{index + 1}.</span> {question.title}
                                                </div>
                                                <div
                                                    className={`ecx-survey-preview__answer-value${
                                                        hasValue ? '' : ' is-empty'
                                                    }`}
                                                >
                                                    {hasValue
                                                        ? Array.isArray(value)
                                                            ? value.join(', ')
                                                            : String(value)
                                                        : 'Not answered'}
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <div className="ecx-survey-preview__empty">No questions yet.</div>
                            )}
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
};

export default EcxSurveyViewer;
